import { useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { BannerGenerator } from './BannerGenerator';
import {
    Share2,
    Copy,
    Check,
    Download,
    Linkedin,
    Mail,
    MessageCircle,
    QrCode,
    ExternalLink,
    Contact,
    Twitter,
    Eye,
    Printer,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ShareProfileDialog = ({ user, profileViews = 0 }) => {
    const [copied, setCopied] = useState(false);
    const [open, setOpen] = useState(false);
    const qrRef = useRef(null);

    const publicUrl = useMemo(() => {
        if (typeof window === 'undefined') return '';
        return `${window.location.origin}/agents/${user?.id}`;
    }, [user?.id]);

    const buildSourceUrl = (sourceId) => {
        try {
            const u = new URL(publicUrl);
            u.searchParams.set('utm_source', sourceId);
            return u.toString();
        } catch {
            return `${publicUrl}?utm_source=${sourceId}`;
        }
    };

    // For social network shares, use the backend OG endpoint which serves rich preview metadata
    // (LinkedIn/WhatsApp/Twitter crawlers don't execute JS — they need server-rendered OG tags).
    // Real users get redirected to the React profile page seamlessly.
    const buildShareUrl = (sourceId) => {
        const baseOg = `${process.env.REACT_APP_BACKEND_URL}/api/og/agent/${user?.id}`;
        try {
            const u = new URL(baseOg);
            u.searchParams.set('utm_source', sourceId);
            return u.toString();
        } catch {
            return `${baseOg}?utm_source=${sourceId}`;
        }
    };

    const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    const headline = user?.agency_name
        ? `${fullName} — Agent immobilier · ${user.agency_name}`
        : `${fullName} — Agent immobilier sur Scalimmo`;

    const shareText = `Découvrez le profil de ${fullName} sur Scalimmo`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(buildSourceUrl('link'));
            setCopied(true);
            toast.success('Lien copié dans le presse-papier');
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            toast.error('Impossible de copier le lien');
        }
    };

    const downloadQR = () => {
        const svg = qrRef.current?.querySelector('svg');
        if (!svg) return;
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
            const size = 1024;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, size, size);
            ctx.drawImage(img, 0, 0, size, size);
            URL.revokeObjectURL(url);

            const a = document.createElement('a');
            a.href = canvas.toDataURL('image/png');
            a.download = `scalimmo_qr_${(fullName || 'agent').replace(/\s/g, '_')}.png`;
            a.click();
            toast.success('QR code téléchargé');
        };
        img.src = url;
    };

    const downloadVCard = () => {
        const link = `${API}/agents/${user?.id}/vcard`;
        const a = document.createElement('a');
        a.href = link;
        a.download = `${(fullName || 'agent').replace(/\s/g, '_')}_scalimmo.vcf`;
        a.click();
        toast.success('Carte de visite téléchargée');
    };

    const shareLinkedIn = () => {
        const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(buildShareUrl('linkedin'))}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const shareWhatsApp = () => {
        const text = `${shareText} : ${buildShareUrl('whatsapp')}`;
        const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const shareTwitter = () => {
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(buildShareUrl('twitter'))}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const shareEmail = () => {
        const subject = encodeURIComponent(`Profil de ${fullName} sur Scalimmo`);
        const body = encodeURIComponent(`Bonjour,\n\nJe vous partage mon profil professionnel Scalimmo : ${buildShareUrl('email')}\n\nÀ très vite,\n${fullName}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    const nativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: headline,
                    text: shareText,
                    url: buildSourceUrl('qr'),
                });
            } catch (_) { /* user dismissed */ }
        } else {
            copyLink();
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-scalimmo-navy/20 hover:bg-scalimmo-navy/5" data-testid="share-profile-btn">
                    <Share2 className="w-4 h-4" />
                    Partager mon profil
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="share-profile-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                        <Share2 className="w-5 h-5 text-scalimmo-gold" />
                        Votre carte de visite digitale
                    </DialogTitle>
                    <DialogDescription>
                        Partagez votre profil partout — chaque consultation booste votre visibilité et votre classement Elite.
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="share" className="mt-2">
                    <TabsList className="grid w-full grid-cols-2 mb-4">
                        <TabsTrigger value="share" className="gap-2" data-testid="tab-share">
                            <Share2 className="w-4 h-4" /> Partage rapide
                        </TabsTrigger>
                        <TabsTrigger value="banners" className="gap-2" data-testid="tab-banners">
                            <Printer className="w-4 h-4" /> Bannières imprimables
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="share" className="m-0">
                        <div className="grid md:grid-cols-2 gap-6">
                    {/* QR Code Section */}
                    <div className="bg-gradient-to-br from-scalimmo-navy to-scalimmo-navy/85 rounded-2xl p-6 text-white flex flex-col items-center justify-center text-center" data-testid="qr-card">
                        <div ref={qrRef} className="bg-white p-4 rounded-xl shadow-soft mb-4">
                            <QRCodeSVG
                                value={buildSourceUrl('qr')}
                                size={180}
                                level="M"
                                fgColor="#0e1d3a"
                                bgColor="#ffffff"
                                includeMargin={false}
                            />
                        </div>
                        <p className="font-heading text-lg font-semibold mb-1">{fullName || 'Votre profil'}</p>
                        <p className="text-xs text-white/70 mb-4 line-clamp-2">{headline}</p>
                        <Button
                            onClick={downloadQR}
                            className="bg-scalimmo-gold hover:bg-scalimmo-gold/90 text-scalimmo-navy gap-2 w-full"
                            data-testid="download-qr-btn"
                        >
                            <QrCode className="w-4 h-4" /> Télécharger le QR
                        </Button>
                    </div>

                    {/* Actions Section */}
                    <div className="space-y-4">
                        {/* Public URL */}
                        <div>
                            <label className="text-sm font-medium text-scalimmo-navy mb-1.5 block">
                                Lien public
                            </label>
                            <div className="flex gap-2">
                                <Input value={publicUrl} readOnly className="font-mono text-xs" data-testid="public-url-input" />
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={copyLink}
                                    className="shrink-0"
                                    data-testid="copy-link-btn"
                                >
                                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
                                    className="shrink-0"
                                    title="Ouvrir le profil"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Profile views indicator */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-scalimmo-navy/5 px-3 py-2 rounded-lg">
                            <Eye className="w-4 h-4 text-scalimmo-navy" />
                            <span>
                                <strong className="text-scalimmo-navy">{profileViews}</strong> vue{profileViews > 1 ? 's' : ''} de profil
                            </span>
                            <Badge className="ml-auto bg-scalimmo-gold/15 text-scalimmo-gold border-scalimmo-gold/30 text-[10px]">
                                Boost ranking
                            </Badge>
                        </div>

                        {/* Social share grid */}
                        <div>
                            <label className="text-sm font-medium text-scalimmo-navy mb-1.5 block">
                                Partager sur
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                <Button variant="outline" onClick={shareLinkedIn} className="gap-2 justify-start" data-testid="share-linkedin">
                                    <Linkedin className="w-4 h-4 text-[#0A66C2]" /> LinkedIn
                                </Button>
                                <Button variant="outline" onClick={shareWhatsApp} className="gap-2 justify-start" data-testid="share-whatsapp">
                                    <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
                                </Button>
                                <Button variant="outline" onClick={shareTwitter} className="gap-2 justify-start" data-testid="share-twitter">
                                    <Twitter className="w-4 h-4 text-[#1DA1F2]" /> X / Twitter
                                </Button>
                                <Button variant="outline" onClick={shareEmail} className="gap-2 justify-start" data-testid="share-email">
                                    <Mail className="w-4 h-4 text-scalimmo-navy" /> Email
                                </Button>
                            </div>
                        </div>

                        {/* vCard */}
                        <Button
                            onClick={downloadVCard}
                            variant="outline"
                            className="w-full gap-2 border-scalimmo-gold/40 hover:bg-scalimmo-gold/10"
                            data-testid="download-vcard-btn"
                        >
                            <Contact className="w-4 h-4 text-scalimmo-gold" />
                            <Download className="w-4 h-4" />
                            Carte de visite (.vcf)
                        </Button>

                        {/* Native share fallback */}
                        {typeof navigator !== 'undefined' && navigator.share && (
                            <Button onClick={nativeShare} className="w-full btn-primary gap-2" data-testid="native-share-btn">
                                <Share2 className="w-4 h-4" /> Partager...
                            </Button>
                        )}
                    </div>
                </div>

                <p className="text-xs text-muted-foreground text-center mt-4">
                    Astuce : passez à l'onglet <strong>Bannières imprimables</strong> pour générer un visuel prêt à imprimer.
                </p>
                    </TabsContent>

                    <TabsContent value="banners" className="m-0">
                        <BannerGenerator user={user} publicUrl={publicUrl} />
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};
